import actions from '@src/redux/actions';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { audioExpired } from '@src/services/project';

export const useAudioPlayerMultiBlock = () => {
  const audioRef = useRef(new Audio());
  const audioInBlockRef = useRef(new Audio());
  const isSeeking = useRef(false);
  const prevCurrentBlockIdRef = useRef(null);
  const prevCurrentPlayingBlockIdRef = useRef(null);
  const prevAudioLinkRef = useRef(null);
  const isInitializedRef = useRef(false);
  const audioPlayingBlockIdRef = useRef(null);
  const isMultiBlockModeRef = useRef(false);
  const loadedAudioLinkRef = useRef(null);

  const {
    currentFilteringStatus,
    currentBlockId,
    currentPlayingBlockId,
    isPlaying,
    isPlayingBlock,
    multiBlock,
    isMultiBlockMode,
  } = useSelector((state) => state.project);
  const isPlayingBlockRef = useRef(isPlayingBlock);
  const isPlayingRef = useRef(isPlaying);
  const audioDurationCache = useRef({});

  const { blocks, blockIds } = multiBlock || {};
  const dispatch = useDispatch();
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDurations, setAudioDurations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isErrorPlaying, setIsErrorPlaying] = useState(false);
  const [blockAudioCurrentTime, setBlockAudioCurrentTime] = useState(0);
  const [blockAudioDuration, setBlockAudioDuration] = useState(0);
  // Bumped when the tab is re-activated, to force the metadata loader to re-run
  // and pick up audio links that arrived (or failed to load) while it was hidden.
  const [reloadMetadataTick, setReloadMetadataTick] = useState(0);

  // Multi-audio integration - Memoized to prevent unnecessary re-creation
  const audioSources = useMemo(() => {
    if (!blocks || !blockIds) return [];

    const sources = blockIds
      .map((blockId) => ({
        audioExpiredAt: blocks[blockId]?.audioExpiredAt,
        audioLink: blocks[blockId]?.audioLink,
        blockId,
      }))
      .filter(
        (block) =>
          block.audioLink &&
          block.audioLink.trim() !== '' &&
          !audioExpired(block.audioExpiredAt),
      );

    return sources;
  }, [blocks, blockIds]);

  const audioLinks = useMemo(
    () => audioSources.map((b) => b.audioLink),
    [audioSources],
  );

  // Stable reference for audio operations
  const audioElementRef = useRef(audioRef.current);

  // Initialize audio element once
  useEffect(() => {
    if (!isInitializedRef.current) {
      audioElementRef.current = audioRef.current;
      isInitializedRef.current = true;
    }
  }, []);

  // Calculate total duration and current progress
  const totalDuration = useMemo(
    () => audioDurations.reduce((sum, duration) => sum + (duration || 0), 0),
    [audioDurations],
  );

  const currentTotalTime = useMemo(() => {
    // When currentBlockId is not in audioSources (e.g. new block focused while playing),
    // fall back to the last loaded audio block to preserve the displayed progress
    const hasCurrentBlockId = audioSources.some(
      (b) => b.blockId === currentBlockId,
    );
    const effectiveBlockId = hasCurrentBlockId
      ? currentBlockId
      : audioPlayingBlockIdRef.current;
    const index = audioSources.findIndex((b) => b.blockId === effectiveBlockId);
    if (index === -1) return 0;
    return (
      audioDurations
        .slice(0, index)
        .reduce((sum, duration) => sum + (duration || 0), 0) + currentTime
    );
  }, [audioDurations, currentBlockId, currentTime, audioSources]);

  // Memoized function to prevent unnecessary re-creation
  const loadAudioMetadata = useCallback(
    async (src) =>
      new Promise((resolve) => {
        const tempAudio = new Audio();
        tempAudio.preload = 'metadata';

        const handleLoadedMetadata = () => {
          resolve(tempAudio.duration);
          tempAudio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };

        tempAudio.addEventListener('loadedmetadata', handleLoadedMetadata);
        tempAudio.src = src;
      }),
    [],
  );

  const deleteOldCachedDurations = () => {
    const currentAudioLinksInCache = Object.keys(audioDurationCache.current);

    // Loop through all audio links in the cache
    // Check if the audio link is in the audio sources and if not, delete it from the cache
    for (let i = 0; i < currentAudioLinksInCache.length; i += 1) {
      const audioLink = currentAudioLinksInCache[i];
      const isExistInAudioSources = audioSources.find(
        (block) => block.audioLink === audioLink,
      );
      if (!isExistInAudioSources) {
        // If the audio link is not in the audio sources, delete it from the cache
        delete audioDurationCache.current[audioLink];
      }
    }
  };

  // Only load metadata when audioSources actually changes (deep comparison)
  useEffect(() => {
    if (audioSources.length) {
      const loadAllMetadata = async () => {
        setIsLoading(true);
        setAudioDurations(new Array(audioSources.length).fill(0));

        // Find all audio sources that are not in the cache
        const uncachedAudioDurations = audioSources.filter(
          (block) => !audioDurationCache.current[block.audioLink],
        );

        // Load metadata for all uncached audio sources
        const promises = uncachedAudioDurations.map((block) =>
          loadAudioMetadata(block.audioLink),
        );
        const newDurations = await Promise.all(promises);

        // Cache the new durations
        for (let i = 0; i < uncachedAudioDurations.length; i += 1) {
          const currentAudioLink = uncachedAudioDurations[i].audioLink;
          audioDurationCache.current[currentAudioLink] = newDurations[i];
        }

        // Update the audio durations
        const allAudioDurations = audioSources.map(
          (block) => audioDurationCache.current[block.audioLink],
        );
        setAudioDurations(allAudioDurations);
        setIsLoading(false);

        // Remove the old cached durations to make sure that it won't be used again
        deleteOldCachedDurations();
      };

      loadAllMetadata();
    } else {
      setAudioDurations([]);
      setIsLoading(false);
    }
  }, [audioLinks.join(','), loadAudioMetadata, reloadMetadataTick]);

  // When the tab is backgrounded during synthesis, the browser can suspend audio
  // metadata loading; the audio links arrive in Redux but their durations never
  // resolve, so the player stays stuck loading. Re-running the loader on
  // re-activation recovers it (cached/valid durations are reused, failed ones are
  // retried).
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setReloadMetadataTick((tick) => tick + 1);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const checkFilterBlockStatus = () => {
      const audio = audioElementRef.current;
      const blockAudio = audioInBlockRef.current;

      audio.pause();
      audio.currentTime = 0;
      blockAudio.pause();
      blockAudio.currentTime = 0;

      setCurrentTime(0);
      dispatch(actions.project.setIsPlayingBlock(false));
      dispatch(actions.project.setIsPlaying(false));
      dispatch(actions.project.setCurrentBlockId(null));
      dispatch(actions.project.setIsMultiBlockMode(false));
    };

    checkFilterBlockStatus();
  }, [currentFilteringStatus]);

  const handleLoadAudio = (currentId) => {
    prevCurrentBlockIdRef.current = currentId;
    audioPlayingBlockIdRef.current = currentId;
    const audio = audioElementRef.current;
    const block = audioSources.find((b) => b.blockId === currentId);
    if (!block) return;
    // Only reload if the audio URL actually changed — avoids resetting currentTime
    // when the same block is resumed after being interrupted (e.g. new block added)
    if (loadedAudioLinkRef.current === block.audioLink) {
      if (isPlayingRef.current) {
        audio.play();
      }
      return;
    }
    loadedAudioLinkRef.current = block.audioLink;
    audio.src = block.audioLink;
    if (isPlayingRef.current) {
      audio.play();
    }
  };

  // When currentBlockId changes to a block not in the audio playlist while playing
  // (e.g. user adds a new block), pause audio without resetting playback position
  useEffect(() => {
    if (
      !currentBlockId ||
      !isPlayingRef.current ||
      !isMultiBlockModeRef.current
    )
      return;
    const isCurrentInSources = audioSources.some(
      (b) => b.blockId === currentBlockId,
    );
    if (!isCurrentInSources) {
      dispatch(actions.project.setIsPlaying(false));
    }
  }, [currentBlockId, dispatch]);

  // Memoized event handlers to prevent unnecessary re-creation
  const handleTimeUpdate = useCallback(() => {
    const time = audioElementRef.current.currentTime;
    setCurrentTime(time);
  }, []);

  const handleEnded = useCallback(() => {
    // Use the ref as source of truth — currentBlockId may be null if the user
    // clicked outside the block to unfocus it while audio was still playing.
    const playingBlockId = audioPlayingBlockIdRef.current || currentBlockId;
    const index = audioSources.findIndex(
      (block) => block.blockId === playingBlockId,
    );
    isSeeking.current = false;

    if (!isMultiBlockMode) {
      // Single block mode: stop after current block ends
      dispatch(actions.project.setIsPlayingBlock(false));
      setCurrentTime(0);
    } else if (index < audioSources.length - 1) {
      // Multi block mode: continue to next block
      const nextBlockId = audioSources[index + 1].blockId;
      dispatch(actions.project.setCurrentBlockId(nextBlockId));
      setCurrentTime(0);
      handleLoadAudio(nextBlockId);
    } else {
      // All audios completed in multi block mode — reset to initial state
      dispatch(actions.project.setIsPlaying(false));
      dispatch(actions.project.setCurrentBlockId(null));
      dispatch(actions.project.setIsMultiBlockMode(false));
      audioPlayingBlockIdRef.current = null;
      loadedAudioLinkRef.current = null;
      setCurrentTime(0);
    }
  }, [currentBlockId, audioSources, isMultiBlockMode, dispatch]);
  const handleEndedInBlock = () => {
    dispatch(actions.project.setIsPlayingBlock(false));
    setBlockAudioCurrentTime(0);
  };

  useEffect(() => {
    const audio = audioInBlockRef.current;

    const handleTimeUpdateInBlock = () => {
      setBlockAudioCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadataInBlock = () => {
      setBlockAudioDuration(audio.duration);
    };

    audio.addEventListener('ended', handleEndedInBlock);
    audio.addEventListener('timeupdate', handleTimeUpdateInBlock);
    audio.addEventListener('loadedmetadata', handleLoadedMetadataInBlock);

    return () => {
      audio.removeEventListener('ended', handleEndedInBlock);
      audio.removeEventListener('timeupdate', handleTimeUpdateInBlock);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadataInBlock);
    };
  }, [handleEnded]);
  useEffect(() => {
    const audio = audioElementRef.current;

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [handleTimeUpdate, handleEnded]);

  useEffect(() => {
    isPlayingBlockRef.current = isPlayingBlock;
  }, [isPlayingBlock]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    isMultiBlockModeRef.current = isMultiBlockMode;
  }, [isMultiBlockMode]);

  useEffect(() => {
    prevCurrentPlayingBlockIdRef.current = currentPlayingBlockId;
  }, [currentPlayingBlockId]);

  useEffect(() => {
    const audio = audioElementRef.current;
    if (isPlaying) {
      audio.play().catch((error) => {
        const ERROR_DUE_TO_EXPIRED_AUDIO_LINK = 'NotSupportedError';
        if (error.name === ERROR_DUE_TO_EXPIRED_AUDIO_LINK) {
          setIsErrorPlaying(true);
        }
      });
    } else {
      audio.pause();
    }
  }, [isPlaying]);
  useEffect(() => {
    const audio = audioInBlockRef.current;
    if (isPlayingBlock) {
      audio.play().catch((error) => {
        const ERROR_DUE_TO_EXPIRED_AUDIO_LINK = 'NotSupportedError';
        if (error.name === ERROR_DUE_TO_EXPIRED_AUDIO_LINK) {
          setIsErrorPlaying(true);
        }
      });
    } else {
      audio.pause();
    }
  }, [isPlayingBlock]);

  useEffect(
    () => () => {
      const audio = audioElementRef.current;
      const blockAudio = audioInBlockRef.current;
      blockAudio.pause();
      blockAudio.currentTime = 0;
      audio.pause();
      audio.currentTime = 0;
      dispatch(actions.project.setCurrentBlockId(null));
    },
    [dispatch],
  );

  const handleChangeVolume = useCallback((value) => {
    audioElementRef.current.volume = value / 100;
  }, []);

  const handlePlayAudioInBlock = (blockId, audioExpiredAt) => {
    const audioHasExpired = audioExpiredAt && moment().isAfter(audioExpiredAt);
    if (audioHasExpired) {
      dispatch(
        actions.project.updateBlockSynthesisInfo(blockId, {
          audioLink: null,
        }),
      );
      return;
    }

    isSeeking.current = false;
    // This is single block play (playing from individual block button)
    dispatch(actions.project.setIsMultiBlockMode(false));

    if (
      prevCurrentPlayingBlockIdRef.current !== blockId ||
      prevAudioLinkRef.current !== blocks[blockId]?.audioLink
    ) {
      const audioLink = blocks[blockId]?.audioLink;
      prevAudioLinkRef.current = audioLink;
      setBlockAudioCurrentTime(0);
      setBlockAudioDuration(0);
      dispatch(actions.project.setAudioLink(audioLink));
      dispatch(actions.project.setCurrentPlayingBlockId(blockId));
      audioInBlockRef.current.src = audioLink;
      audioInBlockRef.current.play();
    }
    dispatch(actions.project.setIsPlayingBlock(true));
    dispatch(actions.project.setIsPlaying(false));
  };

  const handlePlay = useCallback(() => {
    // Find all expired audio and delete the audio link
    // which result in user will have to regenerate the block
    const audioExpiredBlocks = Object.values(blocks || {}).filter((block) =>
      moment().isAfter(block.audioExpiredAt),
    );
    if (audioExpiredBlocks.length > 0) {
      const audioExpiredBlockIds = audioExpiredBlocks.map((block) => block.id);
      const synthesisInfos = audioExpiredBlocks.map((block) => ({
        ...block,
        audioLink: null,
      }));
      dispatch(
        actions.project.updateMultiBlockSynthesisInfo(
          audioExpiredBlockIds,
          synthesisInfos,
        ),
      );
      return;
    }
    if (audioSources.length === 0) return;

    // This is multi-block play (playing from the main audio player)
    dispatch(actions.project.setIsMultiBlockMode(true));
    dispatch(actions.project.setIsPlayingBlock(false));
    dispatch(actions.project.setIsPlaying(true));
    let blockId = currentBlockId;
    const isCurrentInSources = audioSources.some((b) => b.blockId === blockId);
    if (!blockId || !isCurrentInSources) {
      // Resume from the last audio block (e.g. after adding a new block),
      // otherwise start from the first audio block
      const prevAudioBlock = audioPlayingBlockIdRef.current;
      const isPrevInSources =
        prevAudioBlock &&
        audioSources.some((b) => b.blockId === prevAudioBlock);
      blockId = isPrevInSources ? prevAudioBlock : audioSources[0].blockId;
      dispatch(actions.project.setCurrentBlockId(blockId));
    }
    handleLoadAudio(blockId);
  }, [blocks, audioSources, currentBlockId, dispatch]);

  const handlePause = useCallback(() => {
    dispatch(actions.project.setIsPlaying(false));
  }, [dispatch]);

  const handleSeeked = (value) => {
    if (totalDuration === 0) return;

    const targetTime = value;

    // Find which audio this time corresponds to
    let accumulatedTime = 0;
    let targetAudioIndex = 0;
    let targetAudioTime = 0;

    for (let i = 0; i < audioDurations.length; i += 1) {
      const duration = audioDurations[i] || 0;
      if (accumulatedTime + duration >= targetTime) {
        targetAudioIndex = i;
        targetAudioTime = targetTime - accumulatedTime;
        break;
      }
      accumulatedTime += duration;
    }

    isSeeking.current = true;
    const audio = audioElementRef.current;
    const targetBlockId = audioSources[targetAudioIndex]?.blockId;
    dispatch(actions.project.setCurrentBlockId(targetBlockId));
    dispatch(actions.project.setIsMultiBlockMode(true));

    const targetAudioLink = audioSources[targetAudioIndex]?.audioLink;
    loadedAudioLinkRef.current = targetAudioLink;
    audio.src = targetAudioLink;
    prevCurrentBlockIdRef.current = targetBlockId;
    audioPlayingBlockIdRef.current = targetBlockId;
    if (isPlayingRef.current) {
      audio.play();
    }
    audio.currentTime = targetAudioTime;
    setCurrentTime(targetAudioTime);
  };

  const handleSeekedInBlock = (timeSec) => {
    const audio = audioInBlockRef.current;
    audio.currentTime = timeSec;
    setBlockAudioCurrentTime(timeSec);
  };

  // Called when the currently playing block is deleted so the next resume
  // skips to the intended next block regardless of Redux currentBlockId
  const setAudioResumeBlock = useCallback((blockId) => {
    audioPlayingBlockIdRef.current = blockId;
    loadedAudioLinkRef.current = null;
  }, []);

  return {
    audioRef,
    handlePlay,
    handlePause,
    handleSeeked,
    isLoading,
    isErrorPlaying,
    isPlaying,
    currentTime: currentTotalTime,
    duration: totalDuration,
    handleChangeVolume,
    handlePlayAudioInBlock,
    handleLoadAudio,
    blockAudioCurrentTime,
    blockAudioDuration,
    handleSeekedInBlock,
    setAudioResumeBlock,
  };
};
